import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import hubService from '../services/hubService';
import { CheckList } from './RepAccessModal';
import ToggleSwitch from './ToggleSwitch';
import StatusBadge from './StatusBadge';
import { money } from '../utils/money';
import { useLocale, useT } from '../i18n/LanguageContext';

/**
 * MY HUB - one clinic's popup (9/4/26).
 *
 * What a sales rep controls for a clinic they own, and the credit picture next to it:
 *
 *   ORDERING  -> may this clinic's own logins submit orders, or do they only watch while the rep
 *                places orders for them.
 *   PRODUCTS  -> every product in the catalog, or only the ones the rep ticks. A clinic that only
 *                ever buys one dressing should see one dressing in its picker.
 *   CREDIT    -> limit, outstanding, available, and what is past due. Read-only here; an
 *                administrator sets the limit on the Clinics tab.
 *   LOGINS    -> who signs in for this clinic, so the rep can tell whose orders they are looking at.
 *
 * `canManage` false is the clinic login's view and the read-only admin case: the same facts as
 * static text, no controls, no save bar.
 *
 * ONE SAVE BUTTON, ONLY WHAT CHANGED IS SENT. The two settings are two requests on the server, so
 * a change to one must not re-send the other - and if the first lands and the second fails, the
 * dialog keeps the landed one as its new baseline so a retry sends only the failed part.
 */

/** Same members, order-independent. */
const sameSet = (a, b) => a.length === b.length && a.every((v) => b.includes(v));

const idsOf = (rows) => (rows || []).map((p) => String(p?._id ?? p)).filter(Boolean);

/** Everything inside the panel a keyboard can land on. The panel itself is tabIndex -1. */
const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), ' +
  'select:not([disabled]), [tabindex]:not([tabindex="-1"])';

const fullName = (u) => [u?.firstName, u?.lastName].filter(Boolean).join(' ').trim();

const PILL_BASE = 'inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium';

function Tile({ label, value, caption, captionClass = 'text-[#64748b]' }) {
  return (
    <div className="rounded-[10px] border border-[#e2e8f0] bg-[#f7f8fb] px-3 py-2.5 min-w-0">
      <p className="text-[11px] text-[#64748b] uppercase tracking-wide">{label}</p>
      <p className="text-[16px] font-semibold text-[#0f172a] mt-0.5 truncate">{value}</p>
      {caption && <p className={`text-[11px] mt-0.5 ${captionClass}`}>{caption}</p>}
    </div>
  );
}

/**
 * Mounted only while open, keyed by the clinic id from the outer component - so every open starts
 * from the row it was handed and the focus trap runs on mount, exactly like `Modal` in Statements.
 */
function Dialog({ clinic, products, canManage, onClose, onSaved }) {
  const t = useT();
  // One radio group per mounted dialog, so two dialogs on a page could never share a name.
  const modeName = useId();
  const locale = useLocale();

  /**
   * The row as the server last confirmed it. Starts as the card's copy; after a successful PUT it
   * carries that response, so the baseline the edits are compared against is what actually landed.
   */
  const [row, setRow] = useState(clinic);
  const [ordering, setOrdering] = useState(clinic.orderingEnabled !== false);
  const [mode, setMode] = useState(clinic.allowedProducts == null ? 'all' : 'only');
  const [picked, setPicked] = useState(() => idsOf(clinic.allowedProducts));
  const [saving, setSaving] = useState(false);
  // English in state, translated at render: `t` stays out of the save path.
  const [error, setError] = useState('');

  const catalog = useMemo(() => (Array.isArray(products) ? products : []), [products]);
  const catalogIds = useMemo(() => new Set(catalog.map((p) => String(p._id))), [catalog]);
  const pickedInCatalog = useMemo(() => picked.filter((id) => catalogIds.has(id)), [picked, catalogIds]);

  // ---- focus trap (the pattern from Statements' Modal) --------------------------------------
  const panel = useRef(null);
  const returnTo = useRef(null);
  const closeRef = useRef(onClose);
  const savingRef = useRef(saving);
  useEffect(() => {
    closeRef.current = onClose;
    savingRef.current = saving;
  });

  useEffect(() => {
    returnTo.current = document.activeElement;
    panel.current?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (savingRef.current) return;
        e.stopPropagation();
        closeRef.current();
        return;
      }
      if (e.key !== 'Tab') return;
      const root = panel.current;
      if (!root) return;
      const focusable = Array.from(root.querySelectorAll(FOCUSABLE));
      if (focusable.length === 0) {
        e.preventDefault();
        root.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (!root.contains(active) || active === root) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      } else if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      const back = returnTo.current;
      if (back && typeof back.focus === 'function' && document.contains(back)) back.focus();
    };
  }, []);

  // ---- what changed -----------------------------------------------------------------------
  const baseOrdering = row.orderingEnabled !== false;
  const baseAllowed = row.allowedProducts == null ? null : idsOf(row.allowedProducts);
  const orderingChanged = ordering !== baseOrdering;
  const selectionChanged =
    mode === 'all' ? baseAllowed !== null : baseAllowed === null || !sameSet(pickedInCatalog, baseAllowed);
  const dirty = orderingChanged || selectionChanged;

  const togglePick = (id) =>
    setPicked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const save = async () => {
    if (mode === 'only' && pickedInCatalog.length === 0) {
      setError('Pick at least one product, or choose "All products".');
      return;
    }
    setSaving(true);
    setError('');
    let merged = row;
    try {
      if (orderingChanged) {
        const res = await hubService.setOrdering(row._id, ordering);
        const d = res?.data?.data || {};
        merged = {
          ...merged,
          orderingEnabled: typeof d.orderingEnabled === 'boolean' ? d.orderingEnabled : ordering,
          hubUpdatedAt: d.hubUpdatedAt ?? merged.hubUpdatedAt,
          hubUpdatedBy: d.hubUpdatedBy ?? merged.hubUpdatedBy,
        };
        // Landed. If the next request fails, this one must not be sent again on retry.
        setRow(merged);
      }
      if (selectionChanged) {
        const ids = mode === 'all' ? null : pickedInCatalog;
        const res = await hubService.setAllowedProducts(row._id, ids);
        const d = res?.data?.data || {};
        merged = {
          ...merged,
          allowedProducts:
            d.allowedProducts !== undefined
              ? d.allowedProducts
              : ids === null
                ? null
                : catalog.filter((p) => ids.includes(String(p._id))),
          hubUpdatedAt: d.hubUpdatedAt ?? merged.hubUpdatedAt,
          hubUpdatedBy: d.hubUpdatedBy ?? merged.hubUpdatedBy,
        };
        setRow(merged);
      }
      if (onSaved) onSaved(merged);
      onClose();
    } catch {
      // A landed first request must reach the page too, or the card keeps the old value until a
      // reload while the server already has the new one.
      if (merged !== row && onSaved) onSaved(merged);
      setError('Could not save those changes');
    } finally {
      setSaving(false);
    }
  };

  // ---- derived display ----------------------------------------------------------------------
  const changedBy = row.hubUpdatedBy ? fullName(row.hubUpdatedBy) : '';
  const changedOn = row.hubUpdatedAt
    ? new Date(row.hubUpdatedAt).toLocaleDateString(locale, { month: 'long', day: 'numeric', year: 'numeric' })
    : '';
  const changeLine = !row.hubUpdatedAt
    ? t('Not changed yet')
    : changedBy
      ? t('Last changed by {name}, {date}', { name: changedBy, date: changedOn })
      : t('Last changed {date}', { date: changedOn }); // the editor's login was since deleted
  const subtitle = [row.companyName, changeLine].filter(Boolean).join(' · ');

  const ledger = row.ledger || null;
  const hasLimit = typeof row.creditLimit === 'number' && !Number.isNaN(row.creditLimit);
  const outstanding = ledger ? Number(ledger.outstanding) || 0 : null;
  const availableRaw = hasLimit && outstanding !== null ? row.creditLimit - outstanding : null;
  const available = availableRaw === null ? null : Math.max(0, availableRaw);
  const overBy = availableRaw !== null && availableRaw < 0 ? -availableRaw : 0;
  const usedPct =
    hasLimit && outstanding !== null && row.creditLimit > 0
      ? Math.min(100, Math.max(0, (outstanding / row.creditLimit) * 100))
      : 0;
  const barColor = overBy > 0 ? 'bg-[#f23e41]' : usedPct >= 80 ? 'bg-[#c25e16]' : 'bg-[#007a55]';
  const overdueCount = ledger ? Number(ledger.overdueCount) || 0 : 0;

  const allowedNames = (row.allowedProducts || []).map((p) => ({
    id: String(p._id ?? p.name),
    label: `${p.name}${p.sku ? ` · ${p.sku}` : ''}`,
  }));
  const productLabel = (p) => `${p.name}${p.sku ? ` · ${p.sku}` : ''}`;
  const titleId = `hub-dialog-title-${String(row._id)}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={saving ? undefined : onClose} />

      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="relative bg-white rounded-[14px] shadow-xl w-[720px] max-w-[calc(100vw-2rem)] z-10 max-h-[88vh] flex flex-col outline-none"
      >
        <div className="bg-[rgba(226,232,240,0.2)] border-b border-[#e2e8f0] rounded-t-[14px] px-5 py-4 flex items-center justify-between shrink-0">
          <div className="min-w-0">
            <h2 id={titleId} className="text-[16px] font-semibold text-[#0f172a] truncate">
              {row.name}
            </h2>
            <p className="text-[12px] text-[#64748b] mt-0.5">{subtitle}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            aria-label={t('Close')}
            className="w-6 h-6 flex items-center justify-center text-[#64748b] hover:text-[#0f172a] shrink-0 disabled:opacity-50"
          >
            <span className="material-symbols-outlined text-[24px]">cancel</span>
          </button>
        </div>

        <div className="px-5 py-4 overflow-y-auto">
          {/* ---- ORDERING ---- */}
          <section className="mb-5">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-[13px] font-semibold text-[#0f172a]">{t('Ordering')}</h3>
              {!ordering && (
                <span className={`${PILL_BASE} bg-[#ffebec] text-[#f23e41]`}>{t('Read-only')}</span>
              )}
            </div>
            {canManage ? (
              <ToggleSwitch
                checked={ordering}
                onChange={setOrdering}
                disabled={saving}
                label={t('Clinic logins can submit orders')}
                helper={t('Off: they still see every order; you place orders for them.')}
              />
            ) : (
              <p className="text-[13px] text-[#0f172a]">
                {ordering
                  ? t('Clinic logins can submit orders')
                  : t('Clinic logins can see every order but cannot submit one; the sales representative places orders for this clinic.')}
              </p>
            )}
          </section>

          {/* ---- PRODUCTS ---- */}
          <section className="mb-5">
            <div className="flex items-baseline justify-between mb-2">
              <h3 className="text-[13px] font-semibold text-[#0f172a]">{t('Products this clinic can order')}</h3>
              {canManage && mode === 'only' && (
                <span className="text-[11px] text-[#64748b]">
                  {t('{k} of {n} selected', { k: pickedInCatalog.length, n: catalog.length })}
                </span>
              )}
            </div>
            {canManage ? (
              <div>
                <div className="flex flex-wrap gap-x-5 gap-y-1.5 mb-2">
                  <label className="flex items-center gap-2 text-[12px] text-[#0f172a] cursor-pointer">
                    <input
                      type="radio"
                      name={modeName}
                      value="all"
                      checked={mode === 'all'}
                      disabled={saving}
                      onChange={() => setMode('all')}
                    />
                    {t('All products ({n})', { n: catalog.length })}
                  </label>
                  <label className="flex items-center gap-2 text-[12px] text-[#0f172a] cursor-pointer">
                    <input
                      type="radio"
                      name={modeName}
                      value="only"
                      checked={mode === 'only'}
                      disabled={saving}
                      onChange={() => setMode('only')}
                    />
                    {t('Only these · {k} selected', { k: pickedInCatalog.length })}
                  </label>
                </div>
                {mode === 'only' && (
                  <CheckList
                    items={catalog}
                    checked={picked}
                    onToggle={togglePick}
                    labelOf={productLabel}
                    empty="No products in the catalog."
                    searchLabel="Filter products"
                  />
                )}
              </div>
            ) : row.allowedProducts == null ? (
              <p className="text-[13px] text-[#0f172a]">{t('All products ({n})', { n: catalog.length })}</p>
            ) : allowedNames.length === 0 ? (
              <p className="text-[13px] text-[#64748b]">{t('No products in the catalog.')}</p>
            ) : (
              <ul className="grid grid-cols-2 gap-x-4 gap-y-1 text-[12px] text-[#0f172a]">
                {allowedNames.map(({ id, label }) => (
                  <li key={id} className="leading-snug">
                    {label}
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* ---- CREDIT ---- */}
          <section className="mb-5">
            <h3 className="text-[13px] font-semibold text-[#0f172a] mb-2">{t('Credit')}</h3>
            {ledger === null ? (
              <div>
                <div className="grid grid-cols-3 gap-3">
                  <Tile label={t('Credit limit')} value={hasLimit ? money(row.creditLimit) : '—'} />
                </div>
                <p className="text-[12px] text-[#64748b] mt-2">{t('Balance not available.')}</p>
              </div>
            ) : (
              <div>
                <div className="grid grid-cols-3 gap-3">
                  <Tile label={t('Credit limit')} value={hasLimit ? money(row.creditLimit) : '—'} />
                  <Tile label={t('Outstanding')} value={money(outstanding)} />
                  <Tile
                    label={t('Available')}
                    value={available === null ? '—' : money(available)}
                    caption={overBy > 0 ? t('Over limit by {amount}', { amount: money(overBy) }) : ''}
                    captionClass="text-[#f23e41] font-medium"
                  />
                </div>
                {hasLimit ? (
                  <div
                    className="mt-2 h-2 w-full rounded-full bg-[#e2e8f0] overflow-hidden"
                    role="progressbar"
                    aria-label={t('Credit used')}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={Math.round(usedPct)}
                  >
                    <div className={`h-full rounded-full ${barColor}`} style={{ width: `${usedPct}%` }} />
                  </div>
                ) : (
                  <p className="text-[12px] text-[#64748b] mt-2">
                    {t('No credit limit set. An administrator sets it on the Clinics tab.')}
                  </p>
                )}
                <p className={`text-[12px] mt-2 ${overdueCount > 0 ? 'text-[#f23e41] font-medium' : 'text-[#64748b]'}`}>
                  {overdueCount > 0
                    ? t(overdueCount === 1 ? '{n} invoice past due · {amount}' : '{n} invoices past due · {amount}', {
                        n: overdueCount,
                        amount: money(Number(ledger.overdueAmount) || 0),
                      })
                    : t('Nothing past due')}
                </p>
              </div>
            )}
          </section>

          {/* ---- LOGINS ---- */}
          <section>
            <h3 className="text-[13px] font-semibold text-[#0f172a] mb-2">{t('Logins at this clinic')}</h3>
            {(row.logins || []).length === 0 ? (
              <p className="text-[12px] text-[#64748b]">{t('No logins yet.')}</p>
            ) : (
              <ul className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                {row.logins.map((u) => (
                  <li key={String(u._id)} className="flex items-center gap-2 text-[12px] text-[#0f172a] min-w-0">
                    <span className="truncate">
                      {t(u.clinicRole === 'owner' ? '{name} (owner)' : '{name} (user)', { name: fullName(u) || u.email || '' })}
                    </span>
                    {u.isActive === false && <StatusBadge status="inactive" size="small" />}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        {!!error && (
          <div className="px-5 shrink-0">
            <p className="text-[12px] text-red-600 pb-2" role="alert">
              {t(error)}
            </p>
          </div>
        )}

        {canManage && (
          <div className="border-t border-[#e2e8f0] px-5 py-3 flex items-center justify-end gap-2 shrink-0">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="h-[38px] px-4 rounded-[10px] border border-[#e2e8f0] text-xs font-semibold text-[#0f172a] hover:bg-gray-50 disabled:opacity-50"
            >
              {t('Cancel')}
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving || !dirty}
              className="h-[38px] px-5 rounded-[10px] bg-[#0089ff] text-white text-xs font-semibold hover:bg-[#0077e6] disabled:opacity-50"
            >
              {saving ? t('Saving...') : t('Save changes')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * <ClinicHubDialog open clinic={hubRow} products={catalog} canManage onClose onSaved />
 *
 * `clinic` is one element of GET /hub/clinics `data`; `products` the catalog from the same
 * payload; `onSaved` receives the row merged with the server's PUT responses. Renders nothing
 * when closed or without a clinic.
 */
export default function ClinicHubDialog({ open, clinic, products = [], canManage = false, onClose, onSaved }) {
  if (!open || !clinic) return null;
  return (
    <Dialog
      key={String(clinic._id)}
      clinic={clinic}
      products={products}
      canManage={!!canManage}
      onClose={onClose}
      onSaved={onSaved}
    />
  );
}
